import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { send_Otp, resend_Otp, verify_Otp } from "../Services/client";
import { gaLogin, gaIdentifyUser } from "../utils/analytics";
import { ApplicationContext } from "../Context/AppContext";
import { addToCart_Data, getCart_Data } from "./../Services/client";
import "../css/login.css";

const Login = ({ show, setShowLogin, showSignup, callback }) => {
  const MAX_RESENDS = 3;
  const COOLDOWN_SEC = 30;
  const BLOCK_MINUTES = 5;

  const [loginShow, setLoginShow] = useState(show);
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [addToCartResponse, setAddToCartResponse] = useState([]);
  const phoneRef = useRef(null);

  // Resend rate-limiting
  const [resendCount, setResendCount] = useState(0);
  const [resendCooldownSec, setResendCooldownSec] = useState(0);
  const [resendBlocked, setResendBlocked] = useState(false);
  const [resendBlockSec, setResendBlockSec] = useState(0);
  const { t, i18n } = useTranslation();
  const { setCartCount } = ApplicationContext();

  useEffect(() => {
    if (step === "phone") {
      setTimeout(() => phoneRef.current?.focus(), 100);
    }
  }, [step]);

  useEffect(() => {
    if (step === "otp") {
      // Auto-focus first box
      setTimeout(() => {
        document.getElementById("otp-0")?.focus();
      }, 100);

      // Auto-read OTP from SMS (supported browsers)
      if ("OTPCredential" in window) {
        const ac = new AbortController();
        navigator.credentials
          .get({ otp: { transport: ["sms"] }, signal: ac.signal })
          .then((credential) => {
            if (credential && credential.code) {
              const code = credential.code.split("").slice(0, 4);
              while (code.length < 4) code.push("");
              setOtp(code);
            }
          })
          .catch((err) => {
            console.log("OTP Auto-read failed:", err);
          });
        return () => ac.abort();
      }
    }
  }, [step]);

  // Cooldown countdown (30s between resends)
  useEffect(() => {
    if (resendCooldownSec <= 0) return;
    const t = setTimeout(() => setResendCooldownSec((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldownSec]);

  // Block countdown (5 min after limit hit)
  useEffect(() => {
    if (resendBlockSec <= 0) {
      if (resendBlocked) {
        setResendBlocked(false);
        setResendCount(0);
      }
      return;
    }
    const t = setTimeout(() => setResendBlockSec((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendBlockSec, resendBlocked]);

  const resetResendState = () => {
    setResendCount(0);
    setResendCooldownSec(0);
    setResendBlocked(false);
    setResendBlockSec(0);
  };

  const handleClose = () => {
    setLoginShow(false);
    setTimeout(() => setShowLogin(false), 500);
    setStep("phone");
    setPhone("");
    setOtp(["", "", "", ""]);
    resetResendState();
  };

  const validatePhone = () => {
    const newErrors = {};

    if (!phone.trim()) {
      newErrors.mobileNo = t("mobile_required");
      setError(newErrors.mobileNo);
      return Object.keys(newErrors).length === 0;
    }
    if (phone && !/^\d{10}$/.test(phone.replace(/\D/g, ""))) {
      newErrors.mobileNo = t("invalid_mobile");
      setError(newErrors.mobileNo);
      return Object.keys(newErrors).length === 0;
    }

    return Object.keys(newErrors).length === 0;
  };

  const handlePhoneSubmit = async () => {
    if (phone.length === 10 && validatePhone()) {
      try {
        setLoading(true);
        const response = await send_Otp(phone);

        if (response.data.success === true) {
          setStep("otp");
          resetResendState();
        } else {
          setError(response.data.message || t("failed_to_send_otp"));
        }
      } catch (err) {
        setError(err.response?.data?.message || t("failed_to_send_otp"));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleResendOtp = async () => {
    if (resendBlocked || resendCooldownSec > 0) return;

    const newCount = resendCount + 1;
    setResendCount(newCount);

    if (newCount >= MAX_RESENDS) {
      setResendBlocked(true);
      setResendBlockSec(BLOCK_MINUTES * 60);
    } else {
      setResendCooldownSec(COOLDOWN_SEC);
    }

    try {
      setLoading(true);
      const response = await resend_Otp(phone);
      if (response?.data?.success !== true) {
        setError(response?.data?.message || t("failed_to_send_otp"));
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatBlockTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return m > 0 ? `${m}:${String(s).padStart(2, "0")} min` : `${s} sec`;
  };

  const handleOtpChange = (value, index) => {
    if (/^\d?$/.test(value)) {
      setError(null);
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 3) {
        document.getElementById(`otp-${index + 1}`).focus();
      }
      // Auto-submit when all 4 digits are filled
      if (value && newOtp.every((d) => d !== "")) {
        setTimeout(() => handleOtpSubmit(newOtp), 0);
      }
    }
  };

  const handleOtpSubmit = async (otpArray) => {
    const enteredOtp = (otpArray || otp).join("");
    if (enteredOtp.length === 4) {
      try {
        setLoading(true);
        console.log("otp submit", phone);
        const response = await verify_Otp(phone, enteredOtp);
        if (response.status === 200) {
          console.log("response", response?.data?.userAddress?.userAddress);
          const token = response?.data?.token;
          const userMobile = response?.data?.userMobile;
          const userId = response?.data?.userId;

          if (token) {
            localStorage.setItem("token", token);
            localStorage.setItem("userMobile", userMobile);
            localStorage.setItem("userId", userId);
            const userName = response?.data?.name || "";
            if (userName) {
              localStorage.setItem("userName", userName);
            } else {
              localStorage.removeItem("userName");
            }
            gaIdentifyUser({ userId });
            gaLogin({ userId });
            localStorage.setItem(
              "userAddress",
              JSON.stringify(response?.data?.userAddress?.userAddress),
            );

            const deliveryAddress =
              response?.data?.userAddress?.userAddress.filter(
                (el) => el.is_default === 1,
              );
            if (deliveryAddress?.length > 0) {
              localStorage.setItem(
                "deliveryAddress",
                JSON.stringify(deliveryAddress[0]),
              );
            }

            const needsProfile = !response?.data?.name || !response?.data?.email;

            handleClose();
            callback(needsProfile);
            await getUserCartData(userId);
          }
        }
      } catch (err) {
        setError(err.response?.data?.message || t("otp_failed_message"));
      } finally {
        setLoading(false);
      }
    } else {
      setError(t("otp_validation"));
    }
  };

  const handleOtpPaste = (e, index) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").trim();
    if (!/^\d{4}$/.test(pasteData)) return; // Only accept exactly 4 digits

    const newOtp = pasteData.split("");
    setOtp(newOtp);

    // Focus last field after pasting
    setTimeout(() => {
      const lastInput = document.getElementById(`otp-3`);
      if (lastInput) lastInput.focus();
    }, 0);
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (otp[index] === "") {
        // Move to previous box if current is already empty
        if (index > 0) {
          const prevInput = document.getElementById(`otp-${index - 1}`);
          if (prevInput) prevInput.focus();

          const newOtp = [...otp];
          newOtp[index - 1] = "";
          setOtp(newOtp);
        }
      } else {
        // Just clear the current box
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      }
    }
  };

  // useEffect(() => {
  //   const userId = localStorage.getItem("userId");
  //   console.log("addToCartResponse.length", addToCartResponse.length);
  //   if (addToCartResponse.length > 0) {
  //     alert("sss");
  //     getCartData(userId);
  //   }
  // }, [addToCartResponse]);

  const getCartData = async (userId) => {
    const lng = localStorage.getItem("i18nextLng");
    if (lng !== "en" && lng !== "hi") {
      i18n.changeLanguage("en");
    }

    const cartResponse = await getCart_Data(userId, lng);
    if (cartResponse.success === false) {
      console.log("Failed to load cart data");
    } else {
      const returnData = cartResponse?.data?.cartData;
      if (returnData?.length > 0) {
        let liveCartData = returnData?.map((cart, idx) => {
          return {
            productId: cart.productId,
            name: cart.productName,
            price: parseFloat(cart.price).toFixed(2),
            qty: cart.qty,
            img: { id: cart.imgId, url: cart.imgUrl },
            detailId: cart.detailId,
            unit: cart.unit,
            isAdjusted: cart.isAdjusted,
            availQty: cart.availQty,
            unitQty: cart.unitQty,
          };
        });
        localStorage.removeItem("cartItems");
        localStorage.setItem("cartItems", JSON.stringify(liveCartData));
        const totalQty = liveCartData.reduce(
          (sum, item) => sum + (item.qty || 0),
          0,
        );
        setCartCount(totalQty);
        console.log("Cart updated successfully!");
      }
    }
  };

  const getUserCartData = async (userId) => {
    try {
      const storedCart = JSON.parse(localStorage.getItem("cartItems")) || [];

      if (storedCart.length > 0) {
        const response = await addToCart_Data(storedCart, userId);
        console.log("response su", response.data.success);
        if (response?.data?.success === false) {
          console.log("Failed to save data in cart");
        } else {
          if (response?.data?.success === true) {
            getCartData(userId);
          }
        }
      } else {
        getCartData(userId);
      }
    } catch (err) {
      console.error(err.message);
    }
  };

  return (
    <Modal
      show={loginShow}
      onHide={handleClose}
      centered
      animation={true}
      backdrop="static"
      keyboard={false}
      className="custom-login-modal"
    >
      <Modal.Header closeButton className="modal-header-color">
        <Modal.Title className="modal-header-forecolor">
          {t("login")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={(e) => e.preventDefault()}>
          {step === "phone" && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>{t("phone")}</Form.Label>
                <Form.Control
                  type="tel"
                  maxLength={10}
                  value={phone}
                  ref={phoneRef}
                  onChange={(e) => setPhone(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handlePhoneSubmit(); }}
                  placeholder={t("login_placeholder")}
                  required
                  isInvalid={!!error}
                />
                <Form.Control.Feedback type="invalid">
                  {error}
                </Form.Control.Feedback>
              </Form.Group>
              <p className="mt-2 text-center login-sub-text">
                {t("login_mgs_first")} <strong>{t("login_mgs_bold")}</strong>
                {t("login_mgs_last")}
              </p>

              <Button
                className="w-100 modal-header-btn"
                disabled={loading}
                onClick={() => {
                  handlePhoneSubmit();
                }}
              >
                {/* {loading ? "Sending OTP..." : "Send OTP"} */}
                {loading ? t("sending_otp") : t("send_otp")}
              </Button>
            </>
          )}

          {step === "otp" && (
            <>
              <p className="text-center otp-verification">
                {t("otp_verification")}
              </p>
              <p className="text-center login-sub-text">
                {t("otp_sent_to")} <strong>{phone}</strong>
              </p>
              <Row className="mb-3 justify-content-center">
                {otp.map((digit, index) => (
                  <Col xs="auto" key={index}>
                    <Form.Control
                      id={`otp-${index}`}
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(e.target.value, index)}
                      onPaste={(e) => handleOtpPaste(e, index)}
                      onKeyDown={(e) => handleOtpKeyDown(e, index)}
                      className="text-center"
                      style={{
                        width: "45px",
                        height: "45px",
                        fontSize: "20px",
                        margin: "0 5px",
                      }}
                    />
                  </Col>
                ))}
              </Row>

              {error && (
                <p className="text-center text-danger" style={{ fontSize: "13px", marginTop: "-8px" }}>
                  {error}
                </p>
              )}

              <div className="text-center mb-2">
                {resendBlocked ? (
                  <p className="login-sub-text" style={{ color: "#dc3545", fontSize: "12px", margin: 0 }}>
                    You've reached the OTP resend limit. Please try again after {formatBlockTime(resendBlockSec)}.
                  </p>
                ) : (
                  <span className="login-sub-text">
                    {t("didnt_receive_otp")}
                    {resendCooldownSec > 0 ? (
                      <span className="ms-1" style={{ color: "#aaa", fontSize: "13px" }}>
                        Resend in {resendCooldownSec}s
                      </span>
                    ) : (
                      <span
                        onClick={handleResendOtp}
                        className="resend-otp-link ms-1"
                        style={{ cursor: "pointer", color: "#28a745" }}
                      >
                        {t("resend_otp")}
                      </span>
                    )}
                  </span>
                )}
              </div>

              <Button
                onClick={() => {
                  handleOtpSubmit();
                }}
                className="w-100 modal-header-btn"
                disabled={loading}
              >
                {/* {loading ? "Verifying..." : "Verify OTP"} */}
                {loading ? t("verifying") : t("verify_otp")}
              </Button>
            </>
          )}
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default Login;
